import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@contexts/AuthContext";

import axios from "axios";

import api from "@helpers/api";
import Config from "../../../../config";
import { adaptPublicInvitation } from "../../helpers/apiAdapters";

import { INVITE_STATE } from "../enums/inviteStates";

// The invite GET is public, but the shared axios defaults (baseURL + bearer)
// are only configured by AuthContext when a session token exists — so for the
// logged-out visitor this must hit the API by absolute URL. Mirrors the
// `@helpers/api` contract: errors are RETURNED ({ response }), not thrown.
async function publicGet(path) {
  try {
    return await axios.get(`${Config.apiEndpoint}/${path}`);
  } catch (e) {
    return e;
  }
}

// Normalise the account email — the user object stores it as { address } (see
// AuthContext / Sidebar usage), but tolerate a plain string too.
function accountEmailOf(user) {
  const raw = user?.email?.address ?? user?.email ?? "";
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Public mentoring invite (§6.9). Owns the public GET (#12), derives a single
 * INVITE_STATE for the view to switch on, layers the auth gate (must be logged in
 * AND account-email === invited_email) over the actions, and runs accept (#13 →
 * routes into §6.10 attach) / decline (#14 → recusa confirmation).
 *
 * The page renders for logged-out visitors (App.js public branch + AuthProvider
 * skips the redirect, §10); only the action area depends on auth.
 */
export default function useMentoringInvite(token) {
  const navigate = useNavigate();
  const { t } = useTranslation("member");
  const { user, signed, loading: authLoading, goToAuth } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  // A terminal state derived from the fetch (or an action) that supersedes the
  // invitation: not_found | expired | used | declined | error.
  const [terminal, setTerminal] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const requestRef = useRef(0);

  // ── Fetch the public invite ────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setTerminal(INVITE_STATE.NOT_FOUND);
      setLoading(false);
      return;
    }

    const requestId = ++requestRef.current;
    setLoading(true);
    setTerminal(null);
    setInvitation(null);

    (async () => {
      const res = await publicGet(`mentoring/invite/${token}`);
      if (requestId !== requestRef.current) return;

      if (res?.response) {
        setTerminal(mapInviteError(res.response));
      } else {
        const invite = adaptPublicInvitation(res?.data);
        setInvitation(invite);
        if (!invite) setTerminal(INVITE_STATE.NOT_FOUND);
      }
      setLoading(false);
    })();
  }, [token]);

  const program = invitation?.program ?? null;
  const enrollment = invitation?.enrollment ?? null;
  const invitedEmail = (enrollment?.invited_email ?? "").trim();
  const accountEmail = accountEmailOf(user);

  // ── Resolve the single render state ────────────────────────────────────────
  const state = useMemo(() => {
    if (loading) return INVITE_STATE.LOADING;
    if (terminal) return terminal;
    if (!enrollment) return INVITE_STATE.ERROR;

    // A non-pending invite was already answered; an elapsed expiry is expired —
    // even if the GET returned 200, so the page is correct against either
    // a strict 422 backend or a lenient 200-with-status one.
    if (enrollment.status && enrollment.status !== "pending") return INVITE_STATE.USED;
    const expiresAt = enrollment.timestamps?.invite_token_expires_at;
    if (expiresAt && new Date(expiresAt).getTime() < Date.now()) return INVITE_STATE.EXPIRED;
    return INVITE_STATE.VALID;
  }, [loading, terminal, enrollment]);

  // ── Auth gate ──────────────────────────────────────────────────────────────
  const authResolved = !authLoading;
  const isLoggedIn = !!signed;
  const emailMatches =
    isLoggedIn &&
    !!accountEmail &&
    !!invitedEmail &&
    accountEmail.toLowerCase() === invitedEmail.toLowerCase();
  // Logged in, but as the wrong person — the server would 403 accept/decline.
  const emailMismatch = isLoggedIn && !!invitedEmail && !emailMatches;
  const canRespond = state === INVITE_STATE.VALID && authResolved && emailMatches;

  // Surface an action error: a 422 flips the page to its terminal state, a 403
  // explains the email mismatch, anything else is a generic toast.
  const handleActionError = useCallback(
    (response) => {
      const status = response?.status;
      const message = response?.data?.message;
      if (status === 422) {
        setTerminal(mapInviteError(response));
        window.setSnackbar?.(message || t("invite.feedback.unavailable"));
      } else if (status === 403) {
        // Never echo the recipient address back to a different account.
        window.setSnackbar?.(t("invite.feedback.wrongEmail"));
      } else {
        window.setSnackbar?.(message || t("invite.feedback.genericError"));
      }
    },
    [t],
  );

  // ── Accept → §6.10 attach ──────────────────────────────────────────────────
  const accept = useCallback(async () => {
    if (!canRespond || submitting) return;
    setSubmitting(true);

    const res = await api.post(`mentoring/invite/${token}/accept`);

    if (res?.response) {
      handleActionError(res.response);
      setSubmitting(false);
      return;
    }

    window.setSnackbar?.(t("invite.feedback.accepted"));
    const enrollmentId = res?.data?.id ?? enrollment?.id;
    navigate(`/mentoring/${enrollmentId}/attach`);
    // No setSubmitting(false): the navigation unmounts this page.
  }, [canRespond, submitting, token, enrollment, navigate, handleActionError, t]);

  // ── Decline (confirm → #14) ────────────────────────────────────────────────
  const doDecline = useCallback(async () => {
    const res = await api.post(`mentoring/invite/${token}/decline`);

    if (res?.response) {
      handleActionError(res.response);
      return;
    }

    window.setSnackbar?.(t("invite.feedback.declined"));
    setTerminal(INVITE_STATE.DECLINED);
  }, [token, handleActionError, t]);

  const decline = useCallback(() => {
    if (!canRespond || submitting) return;
    window.openConfirm?.({
      title: t("invite.confirmDecline.title"),
      headline: t("invite.confirmDecline.headline"),
      subtitle: t("invite.confirmDecline.subtitle"),
      buttons: [
        { text: t("invite.confirmDecline.confirmButton"), variant: "contained", color: "error", onClick: doDecline },
        { text: t("common:actions.cancel"), variant: "text" },
      ],
    });
  }, [canRespond, submitting, doDecline, t]);

  // ── Auth CTAs ──────────────────────────────────────────────────────────────
  // "Entrar para responder" — bounce to auth, returning to this invite URL.
  const login = useCallback(() => goToAuth?.(), [goToAuth]);

  // "Entrar com outra conta" — drop the wrong session, then log in again here.
  const switchAccount = useCallback(() => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("@user");
    } catch (e) {
      /* ignore */
    }
    goToAuth?.();
  }, [goToAuth]);

  return {
    state,
    program,
    enrollment,
    invitedEmail,
    accountEmail,
    authResolved,
    isLoggedIn,
    emailMatches,
    emailMismatch,
    canRespond,
    submitting,
    accept,
    decline,
    login,
    switchAccount,
  };
}

// Map an `api`-style error response to a terminal INVITE_STATE. 404 = unknown
// token; a 422 carries the business reason (expired vs. already used) in its
// message; anything else is a generic error.
function mapInviteError(response) {
  const status = response?.status;
  const message = response?.data?.message || "";
  if (status === 404) return INVITE_STATE.NOT_FOUND;
  if (status === 422) {
    if (/expirad/i.test(message)) return INVITE_STATE.EXPIRED;
    return INVITE_STATE.USED;
  }
  return INVITE_STATE.ERROR;
}
